import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format, subMonths } from 'date-fns';
import { getCategoryTotals } from '@/services/expenseService';
import { formatCurrency, currentMonth } from '@/utils/formatters';
import { DEFAULT_CATEGORIES } from '@/constants';
import LoadingSpinner from '@/components/shared/LoadingSpinner';

const CAT_MAP = Object.fromEntries(DEFAULT_CATEGORIES.map(c => [c.id, c]));

export default function MonthComparison() {
  const [monthA, setMonthA] = useState(currentMonth());
  const [monthB, setMonthB] = useState(
    format(subMonths(new Date(), 1), 'yyyy-MM')
  );
  const [data, setData]     = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!monthA || !monthB) return;
    setLoading(true);
    Promise.all([getCategoryTotals(monthA), getCategoryTotals(monthB)])
      .then(([a, b]) => {
        const cats = new Set([...Object.keys(a), ...Object.keys(b)]);
        const rows = Array.from(cats).map(cat => ({
          name:  CAT_MAP[cat]?.label ?? cat,
          [monthA]: a[cat] ?? 0,
          [monthB]: b[cat] ?? 0,
          color:  CAT_MAP[cat]?.color ?? '#9E9E9E',
        }));
        setData(rows);
      })
      .finally(() => setLoading(false));
  }, [monthA, monthB]);

  return (
    <div className="space-y-4">
      <div className="card">
        <h3 className="text-xs font-bold text-gray-600 mb-3">📊 Compare Two Months</h3>
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">Month A</label>
            <input type="month" value={monthA} onChange={e => setMonthA(e.target.value)}
              max={currentMonth()} className="input-field text-xs py-1.5" />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">Month B</label>
            <input type="month" value={monthB} onChange={e => setMonthB(e.target.value)}
              max={currentMonth()} className="input-field text-xs py-1.5" />
          </div>
        </div>
      </div>

      {loading ? <LoadingSpinner /> : (
        <div className="card">
          <h3 className="text-xs font-bold text-gray-600 mb-3">
            {monthA} vs {monthB} — Category Comparison
          </h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 30 }}>
              <XAxis dataKey="name" tick={{ fontSize: 8 }} angle={-35} textAnchor="end" />
              <YAxis tick={{ fontSize: 8 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={v => formatCurrency(v)} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey={monthA} fill="#1565C0" radius={[2, 2, 0, 0]} name={monthA} />
              <Bar dataKey={monthB} fill="#90A4AE" radius={[2, 2, 0, 0]} name={monthB} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Table */}
      {!loading && data.length > 0 && (
        <div className="card overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-500 border-b">
                <th className="py-2 text-left">Category</th>
                <th className="py-2 text-right">{monthA}</th>
                <th className="py-2 text-right">{monthB}</th>
                <th className="py-2 text-right">Δ</th>
              </tr>
            </thead>
            <tbody>
              {data.map(row => {
                const delta = row[monthA] - row[monthB];
                return (
                  <tr key={row.name} className="border-b border-gray-50">
                    <td className="py-1.5 text-gray-700">{row.name}</td>
                    <td className="py-1.5 text-right font-medium">{formatCurrency(row[monthA])}</td>
                    <td className="py-1.5 text-right text-gray-400">{formatCurrency(row[monthB])}</td>
                    <td className={`py-1.5 text-right font-semibold ${delta <= 0 ? 'text-success' : 'text-danger'}`}>
                      {delta <= 0 ? '▼' : '▲'} {formatCurrency(Math.abs(delta))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
